import { ComponentProps, FC } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { nodes } from "./nodes";
import theme from './themes/PlaygroundEditorTheme';
import { validateUrl } from "./utils";

// components
import DescriptionInsertMedia from "../Media/DescriptionInsertMedia";

// lexical plugins
import { AutoFocusPlugin } from "./plugins/AutoFocusPlugin";
import ClickableLinkPlugin from "./plugins/ClickableLinkPlugin";
import ExportPlugin from './plugins/ExportPlugin';
import FormHelperText from '@mui/material/FormHelperText';
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import ImportPlugin from './plugins/ImportPlugin';
import { InsertImagePlugin } from "./plugins/InsertImagePlugin";
import { InsertMentionPlugin } from "./plugins/InsertMentionPlugin";
import { InsertReplyPlugin } from "./plugins/InsertReplyPlugin";
import { InsertVideoPlugin } from "./plugins/InsertVideoPlugin";
import { LinkPlugin as LexicalLinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { PasteMediaPlugin } from "./plugins/PasteMediaPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ToolbarPlugin } from "./plugins/ToolbarPlugin";

// mui 
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const initialConfig: ComponentProps<typeof LexicalComposer>["initialConfig"] = {
    namespace: "ConnectEditor",
    onError: (error) => console.error(error),
    nodes: nodes,
    theme: theme,
};

const Editor: FC<{
    defaultContentAsJSON?: string;
    error?: boolean;
    helperText?: string;
    isEditable: boolean;
    replyContent?: { message: string, userName: string, userId: number };
    setCurrentContentAsJSON?: React.Dispatch<React.SetStateAction<string>>;
}> = ({
    defaultContentAsJSON,
    error,
    helperText,
    isEditable,
    replyContent,
    setCurrentContentAsJSON,
}) => {
    const exportAsJSON = (contentAsJSON: string) => {
        if (setCurrentContentAsJSON) {
            setCurrentContentAsJSON(contentAsJSON);
        }
    };

    return (
        <>
            {/*メディアの挿入について*/}
            {isEditable &&
                <DescriptionInsertMedia />
            }

            {/*エディター*/}
            <LexicalComposer
                initialConfig={{ ...initialConfig, editable: isEditable }}
            >
                <Box sx={{
                    px: 2,
                    py: 1,
                    borderWidth: isEditable ? 1 : 0,
                    borderStyle: "solid",
                    borderColor: (theme) => error ? theme.palette.error.main : theme.palette.grey[400],
                    borderRadius: 1,
                    '& .contentEditable': {
                        outline: "none",
                    }
                }}>
                    <ToolbarPlugin />
                    <Box sx={{
                        position: "relative",
                        minHeight: isEditable ? 160 : "unset",
                        '& .contentEditable': {
                            outline: "none",
                        }
                    }}>
                        <RichTextPlugin
                            contentEditable={<ContentEditable className="contentEditable"/>}
                            placeholder={
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        color: (theme) => theme.palette.grey[500],
                                        top: 0,
                                        left: 0,
                                        pointerEvents: "none",
                                        userSelect: "none",
                                    }}
                                >{isEditable ? "入力してください" : "本文がありません"}</Typography>
                            }
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                    </Box>
                    <AutoFocusPlugin />
                    <ClickableLinkPlugin /> {/* 最新のPlaygroundでは使用されていない */}
                    <ExportPlugin exportAsJSON={exportAsJSON} />
                    <HistoryPlugin />
                    <ImportPlugin defaultContentAsJSON={defaultContentAsJSON} />
                    <InsertImagePlugin />
                    <InsertMentionPlugin />
                    <InsertReplyPlugin replyContent={replyContent} />
                    <InsertVideoPlugin />
                    <LexicalLinkPlugin validateUrl={validateUrl} />
                    <ListPlugin />
                    <PasteMediaPlugin />
                </Box>
                {helperText !== "" ? (
                    <FormHelperText error={true}>{helperText}</FormHelperText>
                ) : <></>}
            </LexicalComposer>
        </>
    );
};

export default Editor;